<script setup>
  import { ref, computed, watch } from "vue";
  import { usePermissionStore } from "@/stores/usePermissionStore";
  import DeleteConfirmModal from "@/components/Generics/DeleteConfirmModal.vue";
  
  const props = defineProps({
    permission: { type: Object, required: true },
  });
  
  const emit = defineEmits(["close", "refresh"]);
  const store = usePermissionStore();
  
  const isDeleting = ref(false);
  
  // ✅ Congela alvo (ID) e texto (nome/label)
  const frozenPermissionId = ref(null);
  const frozenPermissionName = ref("");
  
  function getPermissionId() {
    return props.permission?.permissionId ?? props.permission?.id ?? null;
  }
  
  function getPermissionName() {
    const name = props.permission?.permissionName || props.permission?.name || "";
    const id = getPermissionId();
    const label = (name?.trim?.() || "");
    return label || (id ? `#${id}` : "");
  }
  
  function freezeSnapshot() {
    frozenPermissionId.value = getPermissionId();
    frozenPermissionName.value = getPermissionName();
  }
  
  // ✅ Mantém snapshot atualizado ENQUANTO NÃO está deletando
  watch(
    () => [props.permission?.permissionId, props.permission?.id, props.permission?.permissionName, props.permission?.name],
    () => {
      if (!isDeleting.value) freezeSnapshot();
    },
    { immediate: true }
  );
  
  const modalTitle = "Apagar Permissão";
  
  const messageTitle = computed(() => {
    const name = frozenPermissionName.value;
    return name
      ? `Você está prestes a apagar a permissão "${name}".`
      : "Você está prestes a apagar esta permissão.";
  });
  
  const messageSubtitle = computed(() => "Esta ação não pode ser desfeita.");
  
  function buildErrorMessage(error) {
    return (
      error?.response?.data?.message ||
      error?.message ||
      "Ocorreu um erro ao excluir a permissão."
    );
  }
  
  async function onConfirmDelete() {
    // ✅ congela ANTES de deletar (texto + alvo)
    freezeSnapshot();
    isDeleting.value = true;
  
    try {
      if (!frozenPermissionId.value) {
        throw new Error("ID da permissão inválido para exclusão.");
      }
  
      await store.deletePermissionById(frozenPermissionId.value);
    } catch (error) {
      isDeleting.value = false; // permite tentar de novo
      throw new Error(buildErrorMessage(error)); // toast vem do DeleteConfirmModal
    }
  }
  
  function handleSuccess() {
    emit("refresh");
    emit("close");
  }
  </script>
  
  <template>
    <DeleteConfirmModal
      :show="true"
      :title="modalTitle"
      :messageTitle="messageTitle"
      :messageSubtitle="messageSubtitle"
      confirmText="Sim, apagar"
      cancelText="Cancelar"
      loadingText="Apagando..."
      :onConfirm="onConfirmDelete"
      @success="handleSuccess"
      @close="!isDeleting && emit('close')"
    />
  </template>
  